import { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Stack, TextField, Typography, CircularProgress, Chip,
  Autocomplete, Tab, Tabs, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon, AutoFixHigh as GenerateIcon,
  Delete as DeleteIcon, Edit as EditIcon,
} from '@mui/icons-material';
import { useBeneficiosStore } from '../store/beneficiosStore';
import { TrabajadorService } from '../../trabajadores/services/trabajadorService';
import type { Trabajador } from '../../trabajadores/types/trabajador';
import type { Vacacion, RegistrarGoceRequest } from '../types/beneficios';
import GoceVentaDialog from './GoceVentaDialog';
import VacacionSummaryCards from './VacacionSummaryCards';
import VacacionesPorVencerTab from './VacacionesPorVencerTab';
import VacacionesVistaGeneralTab from './VacacionesVistaGeneralTab';

const initialVacacion: Vacacion = {
  idVacacion: 0, idTrabajador: 0,
  periodoDesde: '', periodoHasta: '',
  diasGanados: 30, diasGozados: 0, diasVendidos: 0, diasPendientes: 30,
  fechaInicioGoce: null, fechaFinGoce: null,
  remuneracionVacacional: 0, montoVendido: 0,
  estado: 'PENDIENTE', observaciones: null,
  fechaVencimiento: null, esMicroempresa: false, remuneracionIntegra: false,
  goces: [],
};

export default function VacacionesPanel() {
  const {
    vacaciones, cuentaCorriente, loading, error,
    loadCuentaCorriente, generarPeriodos, guardarVacacion,
    registrarGoce, eliminarGoce, setError,
  } = useBeneficiosStore();

  const [tabIndex, setTabIndex] = useState(0);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'info' | 'warning' } | null>(null);
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [selectedTrabajador, setSelectedTrabajador] = useState<Trabajador | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [formData, setFormData] = useState<Vacacion>(initialVacacion);
  const [openGoceDialog, setOpenGoceDialog] = useState(false);
  const [selectedPeriodo, setSelectedPeriodo] = useState<Vacacion | null>(null);

  // Detalle goces expandido
  const [expandedPeriodo, setExpandedPeriodo] = useState<number | null>(null);

  useEffect(() => {
    TrabajadorService.getAll().then(data => setTrabajadores(data.filter(t => t.activo))).catch(() => {});
  }, []);

  const handleSelectTrabajador = (_: unknown, val: Trabajador | null) => {
    setSelectedTrabajador(val);
    setExpandedPeriodo(null);
    if (val) loadCuentaCorriente(val.idTrabajador);
  };

  const handleGenerarPeriodos = async () => {
    if (!selectedTrabajador) return;
    setMessage(null);
    const count = await generarPeriodos(selectedTrabajador.idTrabajador);
    if (count > 0) {
      setMessage({ text: `Se generaron ${count} periodo(s) vacacional(es)`, severity: 'success' });
    } else if (count === 0) {
      setMessage({ text: 'No se generaron periodos nuevos. El trabajador debe cumplir al menos 1 año de servicio para generar un periodo.', severity: 'info' });
    }
    // count === -1 means error, already handled by store
    await loadCuentaCorriente(selectedTrabajador.idTrabajador);
  };

  const handleNuevoPeriodo = () => {
    if (!selectedTrabajador) return;
    setFormData({ ...initialVacacion, idTrabajador: selectedTrabajador.idTrabajador });
    setOpenEditDialog(true);
  };

  const handleEditPeriodo = (vacacion: Vacacion) => {
    setFormData(vacacion);
    setOpenEditDialog(true);
  };

  const handleGuardarPeriodo = async () => {
    await guardarVacacion(formData);
    setOpenEditDialog(false);
    if (selectedTrabajador) loadCuentaCorriente(selectedTrabajador.idTrabajador);
  };

  const handleOpenGoce = (vacacion: Vacacion) => {
    setSelectedPeriodo(vacacion);
    setOpenGoceDialog(true);
  };

  const handleSaveGoce = async (request: RegistrarGoceRequest) => {
    if (!selectedTrabajador) return;
    await registrarGoce(request, selectedTrabajador.idTrabajador);
    loadCuentaCorriente(selectedTrabajador.idTrabajador);
  };

  const handleEliminarGoce = async (idVacacionGoce: number) => {
    if (!selectedTrabajador) return;
    await eliminarGoce(idVacacionGoce, selectedTrabajador.idTrabajador);
    loadCuentaCorriente(selectedTrabajador.idTrabajador);
  };

  const estadoColor = (estado: string) => {
    switch (estado) {
      case 'PENDIENTE': return 'warning';
      case 'EN_GOCE': return 'info';
      case 'GOZADA': return 'success';
      case 'TRUNCA': return 'error';
      case 'VENCIDA': return 'error';
      default: return 'default';
    }
  };

  const vencimientoChip = (row: Vacacion) => {
    if (!row.fechaVencimiento) return null;
    const dias = Math.floor((new Date(row.fechaVencimiento).getTime() - Date.now()) / 86400000);
    if (dias < 0) return <Chip label="VENCIDO" size="small" color="error" />;
    if (dias <= 30) return <Chip label={`${dias}d`} size="small" color="error" />;
    if (dias <= 60) return <Chip label={`${dias}d`} size="small" color="warning" />;
    if (dias <= 90) return <Chip label={`${dias}d`} size="small" color="info" />;
    return <Typography variant="caption">{new Date(row.fechaVencimiento).toLocaleDateString('es-PE')}</Typography>;
  };

  const periodoColumns: GridColDef[] = [
    { field: 'periodoDesde', headerName: 'Desde', width: isMobile ? 90 : 105,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
    ...(!isMobile ? [
      { field: 'periodoHasta', headerName: 'Hasta', width: 105,
        valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
      { field: 'diasGanados', headerName: 'Ganados', width: 75, type: 'number' as const },
      { field: 'diasGozados', headerName: 'Gozados', width: 75, type: 'number' as const },
      { field: 'diasVendidos', headerName: 'Vendidos', width: 80, type: 'number' as const },
    ] : []),
    { field: 'diasPendientes', headerName: 'Pend.', width: 65, type: 'number',
      cellClassName: 'font-bold' },
    { field: 'estado', headerName: 'Estado', width: isMobile ? 85 : 100,
      renderCell: (params) => <Chip label={params.value} size="small" color={estadoColor(params.value as string)} /> },
    ...(!isMobile ? [
      { field: 'fechaVencimiento', headerName: 'Vencimiento', width: 110,
        renderCell: (params: any) => vencimientoChip(params.row as Vacacion) },
    ] : []),
    { field: 'acciones', headerName: '', width: isMobile ? 80 : 120, sortable: false, filterable: false,
      renderCell: (params) => {
        const vac = params.row as Vacacion;
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Tomar / Vender">
              <span>
                <IconButton size="small" color="primary" disabled={vac.diasPendientes <= 0}
                  onClick={(e) => { e.stopPropagation(); handleOpenGoce(vac); }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Editar periodo">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditPeriodo(vac); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  const goceColumns: GridColDef[] = [
    { field: 'tipoDevengue', headerName: 'Tipo', width: 80,
      renderCell: (params) => (
        <Chip label={params.value} size="small"
          color={params.value === 'GOCE' ? 'success' : 'info'} variant="outlined" />
      ),
    },
    { field: 'fechaInicio', headerName: 'Inicio', width: isMobile ? 90 : 105,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
    ...(!isMobile ? [
      { field: 'fechaFin', headerName: 'Fin', width: 105,
        valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
      { field: 'diasCalendario', headerName: 'D.Cal', width: 65, type: 'number' as const },
      { field: 'diasInhabiles', headerName: 'Inhab.', width: 65, type: 'number' as const },
    ] : []),
    { field: 'diasEfectivos', headerName: 'Efect.', width: 65, type: 'number' },
    ...(!isMobile ? [
      { field: 'montoCalculado', headerName: 'Monto', width: 100, type: 'number' as const,
        valueFormatter: (value: number) => value > 0 ? value.toFixed(2) : '-' },
      { field: 'observaciones', headerName: 'Observaciones', flex: 1, minWidth: 120 },
    ] : []),
    { field: 'eliminar', headerName: '', width: 50, sortable: false,
      renderCell: (params) => (
        <Tooltip title="Eliminar">
          <IconButton size="small" color="error"
            onClick={() => handleEliminarGoce(params.row.idVacacionGoce)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const gocesDelPeriodo = vacaciones.find(v => v.idVacacion === expandedPeriodo)?.goces ?? [];

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab label={isMobile ? 'Trabajador' : 'Por Trabajador'} />
          <Tab label={isMobile ? 'Alertas' : 'Alertas Vencimiento'} />
          <Tab label={isMobile ? 'General' : 'Vista General'} />
        </Tabs>
      </Box>

      {/* Tab 0: Por Trabajador */}
      {tabIndex === 0 && (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <Autocomplete
              size="small" sx={{ width: { xs: '100%', sm: 380 } }}
              options={trabajadores}
              getOptionLabel={(t) => `${t.personal?.personaContacto ?? ''} (${t.personal?.identificacion ?? ''})`}
              value={selectedTrabajador}
              onChange={handleSelectTrabajador}
              renderInput={(params) => <TextField {...params} label="Buscar trabajador" placeholder="Nombre o documento..." />}
              isOptionEqualToValue={(opt, val) => opt.idTrabajador === val.idTrabajador}
              loading={trabajadores.length === 0}
              fullWidth={isMobile}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              {loading && <CircularProgress size={20} />}
              {selectedTrabajador && (
                <>
                  <Button variant="outlined" startIcon={!isMobile ? <GenerateIcon /> : undefined} onClick={handleGenerarPeriodos}
                    size="small" disabled={loading}>{isMobile ? 'Generar' : 'Generar Periodos'}</Button>
                  <Button variant="outlined" startIcon={!isMobile ? <AddIcon /> : undefined} onClick={handleNuevoPeriodo}
                    size="small">{isMobile ? 'Manual' : 'Registrar Manual'}</Button>
                </>
              )}
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
          )}
          {message && (
            <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>
          )}

          {selectedTrabajador && (
            <>
              <VacacionSummaryCards cuenta={cuentaCorriente} />

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Periodos Vacacionales
              </Typography>
              <DataGrid
                rows={vacaciones}
                columns={periodoColumns}
                getRowId={(row) => row.idVacacion}
                autoHeight
                density="compact"
                disableRowSelectionOnClick
                pageSizeOptions={[10]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                onRowClick={(params) => setExpandedPeriodo(
                  expandedPeriodo === params.row.idVacacion ? null : params.row.idVacacion
                )}
                sx={{
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  '& .font-bold': { fontWeight: 700 },
                }}
              />

              {/* Detalle de goces del periodo seleccionado */}
              {expandedPeriodo && gocesDelPeriodo.length > 0 && (
                <Box sx={{ mt: 2, pl: 2, borderLeft: 3, borderColor: 'primary.light' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Detalle de Goces / Ventas (Periodo seleccionado)
                  </Typography>
                  <DataGrid
                    rows={gocesDelPeriodo}
                    columns={goceColumns}
                    getRowId={(row) => row.idVacacionGoce}
                    autoHeight
                    density="compact"
                    disableRowSelectionOnClick
                    hideFooter={gocesDelPeriodo.length <= 5}
                    pageSizeOptions={[5]}
                    initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  />
                </Box>
              )}
              {expandedPeriodo && gocesDelPeriodo.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 2 }}>
                  Este periodo no tiene registros de goce/venta aún. Use el botón "+" para registrar.
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {/* Tab 1: Alertas Vencimiento */}
      {tabIndex === 1 && <VacacionesPorVencerTab />}

      {/* Tab 2: Vista General */}
      {tabIndex === 2 && <VacacionesVistaGeneralTab />}

      {/* Dialog: Goce/Venta */}
      <GoceVentaDialog
        open={openGoceDialog}
        onClose={() => setOpenGoceDialog(false)}
        vacacion={selectedPeriodo}
        onSave={handleSaveGoce}
      />

      {/* Dialog: Editar Periodo Manual */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{formData.idVacacion === 0 ? 'Registrar' : 'Editar'} Periodo Vacacional</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField size="small" label="Periodo Desde" type="date" fullWidth
                value={formData.periodoDesde?.split('T')[0] || ''}
                onChange={(e) => setFormData({ ...formData, periodoDesde: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }} />
              <TextField size="small" label="Periodo Hasta" type="date" fullWidth
                value={formData.periodoHasta?.split('T')[0] || ''}
                onChange={(e) => setFormData({ ...formData, periodoHasta: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField size="small" label="Días Ganados" type="number" value={formData.diasGanados}
                onChange={(e) => setFormData({ ...formData, diasGanados: Number(e.target.value) })} />
              <TextField size="small" label="Días Gozados" type="number" value={formData.diasGozados}
                onChange={(e) => setFormData({ ...formData, diasGozados: Number(e.target.value) })} />
              <TextField size="small" label="Días Vendidos" type="number" value={formData.diasVendidos}
                onChange={(e) => setFormData({ ...formData, diasVendidos: Number(e.target.value) })} />
            </Stack>
            <TextField size="small" label="Observaciones" multiline rows={2}
              value={formData.observaciones || ''}
              onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardarPeriodo} disabled={loading}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
